// src\pages\admin\ProjectsManagement.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePortfolio } from '../../hooks/usePortfolio';
import {
  FiPlus,
  FiSearch,
  FiGrid,
  FiList,
  FiEdit2,
  FiEye,
  FiTrash2,
  FiHeart,
  FiFilter,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiShare2,
  FiMousePointer,
  FiCheckSquare,
  FiSquare,
  FiX,
  FiFolder,
  FiPenTool,
} from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/useToast';
import ProjectCard from '../../components/admin/ProjectCard';
import ProjectModal from '../../components/admin/ProjectModal';
import ProjectPreviewModal from '../../components/admin/ProjectPreviewModal';

// =============================================
// TAB CONFIG
// =============================================
const TABS = [
  {
    id: 'portfolio',
    label: 'Portfolio',
    icon: FiFolder,
    workType: 'portfolio',
    color: '#3b82f6',
  },
  {
    id: 'illustration',
    label: 'Illustrations',
    icon: FiPenTool,
    workType: 'illustration',
    color: '#ec4899',
  },
];

function ProjectsManagement() {
  const [activeTab, setActiveTab] = useState('portfolio');

  // ✅ Both hooks preload their data on mount
  const portfolioHook = usePortfolio('portfolio', { autoLoad: true });
  const illustrationHook = usePortfolio('illustration', { autoLoad: true });

  const { actualTheme } = useTheme();
  const { showToast } = useToast();
  const isDark = actualTheme === 'dark';

  // =============================================
  // INSTANT BADGE COUNTS STATE
  // =============================================
  const [tabCounts, setTabCounts] = useState({
    portfolio: null, // null = not yet loaded
    illustration: null,
  });

  // =============================================
  // MODAL STATE
  // =============================================
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeletingSingle, setIsDeletingSingle] = useState(false);

  // Bulk selection
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const projectsPerPage = 9;

  // =============================================
  // ACTIVE HOOK (depends on tab)
  // =============================================
  const activeHook =
    activeTab === 'illustration' ? illustrationHook : portfolioHook;
  const activeWorkType = activeTab; // 'portfolio' or 'illustration'

  const {
    allProjects,
    addProject,
    updateProject,
    deleteProject,
    refresh,
    loading,
    selectedType,
    setSelectedType,
    uploadSingleImage,
    deleteProjectImage,
    deleteTempImage,
  } = activeHook;

  const projects = allProjects || [];

  // =============================================
  // FETCH INSTANT COUNTS ON MOUNT
  // =============================================
  const fetchCounts = useCallback(async () => {
    try {
      console.log('📊 [Admin] Fetching instant counts...');
      const res = await fetch('/api/portfolio?action=counts');
      const data = await res.json();
      if (data.success) {
        console.log(
          `📊 [Admin] Counts received - portfolio: ${data.portfolio}, illustration: ${data.illustration}`,
        );
        setTabCounts({
          portfolio: data.portfolio,
          illustration: data.illustration,
        });
      }
    } catch (err) {
      console.error('❌ [Admin] Failed to fetch counts:', err);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Re-fetch counts whenever either hook finishes loading
  // (keeps counts in sync after create/delete operations)
  useEffect(() => {
    if (
      portfolioHook.allProjects.length > 0 ||
      illustrationHook.allProjects.length > 0
    ) {
      // Don't refetch — just update local counts from hook state
      setTabCounts({
        portfolio: portfolioHook.allProjects.length,
        illustration: illustrationHook.allProjects.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioHook.allProjects.length, illustrationHook.allProjects.length]);

  // =============================================
  // TAB BADGE COUNT RESOLVER
  // Priority: hook data (accurate) > instant count > 0
  // =============================================
  const getTabCount = tabId => {
    const hookProjects =
      tabId === 'illustration'
        ? illustrationHook.allProjects
        : portfolioHook.allProjects;

    // If the hook has loaded real data, use its length (most accurate)
    if (hookProjects.length > 0) return hookProjects.length;

    // Otherwise, show the instant count if available
    const instant = tabCounts[tabId];
    if (instant !== null && instant !== undefined) return instant;

    // Fallback
    return 0;
  };

  // =============================================
  // RESET FILTERS ON TAB CHANGE
  // =============================================
  useEffect(() => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedType('all');
    setCurrentPage(1);
    setSelectedProjects([]);
    setIsAllSelected(false);
  }, [activeTab, setSelectedType]);

  // =============================================
  // STATS (per tab)
  // =============================================
  const totalProjects = projects.length;
  const totalImages = projects.reduce(
    (sum, p) => sum + (p.images?.length || 0),
    0,
  );
  const totalLikes = projects.reduce((sum, p) => sum + (p.likes || 0), 0);
  const totalViews = projects.reduce((sum, p) => sum + (p.views || 0), 0);
  const totalClicks = projects.reduce((sum, p) => sum + (p.clicks || 0), 0);
  const totalShares = projects.reduce((sum, p) => sum + (p.shares || 0), 0);

  // =============================================
  // CATEGORIES (per tab)
  // =============================================
  const portfolioCategories = [
    { id: 'all', name: 'All Projects', count: projects.length },
    {
      id: 'branding',
      name: 'Brand Design',
      count: projects.filter(p => p.category === 'branding').length,
    },
    {
      id: 'product',
      name: 'Product Visual',
      count: projects.filter(p => p.category === 'product').length,
    },
    {
      id: 'others',
      name: 'Other Works',
      count: projects.filter(p => p.category === 'others').length,
    },
  ];

  const illustrationCategories = [
    { id: 'all', name: 'All Illustrations', count: projects.length },
    {
      id: 'character',
      name: 'Character Art',
      count: projects.filter(p => p.category === 'character').length,
    },
    {
      id: 'scene',
      name: 'Scene Art',
      count: projects.filter(p => p.category === 'scene').length,
    },
    {
      id: 'concept',
      name: 'Concept Art',
      count: projects.filter(p => p.category === 'concept').length,
    },
    {
      id: 'others',
      name: 'Other Works',
      count: projects.filter(p => p.category === 'others').length,
    },
  ];

  const categories =
    activeTab === 'illustration' ? illustrationCategories : portfolioCategories;

  const types = [
    { id: 'all', name: 'All Types', count: projects.length },
    {
      id: 'project',
      name: 'Projects',
      count: projects.filter(p => p.type === 'project').length,
    },
    {
      id: 'random',
      name: 'Random Works',
      count: projects.filter(p => p.type === 'random').length,
    },
  ];

  // =============================================
  // FILTERING
  // =============================================
  const filteredProjects = projects.filter(project => {
    const matchesSearch =
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.tags || []).some(tag =>
        tag.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    const matchesCategory =
      selectedCategory === 'all' || project.category === selectedCategory;
    const matchesType = selectedType === 'all' || project.type === selectedType;
    return matchesSearch && matchesCategory && matchesType;
  });

  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(
    indexOfFirstProject,
    indexOfLastProject,
  );
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

  // =============================================
  // SELECTION HANDLERS
  // =============================================
  const toggleProjectSelection = projectId => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedProjects([]);
    } else {
      const currentIds = currentProjects.map(p => p.id);
      setSelectedProjects(currentIds);
    }
    setIsAllSelected(!isAllSelected);
  };

  const clearSelection = () => {
    setSelectedProjects([]);
    setIsAllSelected(false);
  };

  useEffect(() => {
    if (currentProjects.length > 0) {
      const currentIds = currentProjects.map(p => p.id);
      const allSelected = currentIds.every(id => selectedProjects.includes(id));
      setIsAllSelected(allSelected);
    } else {
      setIsAllSelected(false);
    }
  }, [selectedProjects, currentProjects]);

  // =============================================
  // CRUD HANDLERS
  // =============================================
  const handleAddProject = async (formData, imageFiles) => {
    setSaving(true);
    try {
      const filesToUpload = imageFiles || [];
      const finalFormData = { ...formData, workType: activeWorkType };
      await addProject(finalFormData, filesToUpload);
      setIsAddModalOpen(false);

      // Refresh counts after create
      await fetchCounts();

      showToast(
        `${activeWorkType === 'illustration' ? 'Illustration' : 'Project'} created successfully!`,
        'success',
      );
    } catch (error) {
      console.error('Error adding project:', error);
      showToast(error.message || 'Failed to add project', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditProject = async (formData, imageFiles) => {
    setSaving(true);
    try {
      await updateProject(selectedProject.id, formData, imageFiles);
      setIsEditModalOpen(false);
      setSelectedProject(null);

      // Refresh counts after edit (workType might have changed)
      await fetchCounts();

      showToast('Updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating project:', error);
      showToast(error.message || 'Failed to update project', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    setIsDeletingSingle(true);
    try {
      await deleteProject(projectToDelete.id);
      setShowDeleteModal(false);
      setProjectToDelete(null);
      setSelectedProjects(prev => prev.filter(id => id !== projectToDelete.id));

      // Refresh counts after delete
      await fetchCounts();

      showToast('Deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting project:', error);
      showToast(error.message || 'Failed to delete project', 'error');
    } finally {
      setIsDeletingSingle(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProjects.length === 0) {
      showToast('No projects selected for deletion.', 'warning');
      return;
    }

    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (const id of selectedProjects) {
        try {
          await deleteProject(id);
          successCount++;
        } catch (error) {
          console.error(`Failed to delete project ${id}:`, error);
          failCount++;
        }
      }

      // Refresh counts after bulk delete
      await fetchCounts();
      clearSelection();

      if (failCount === 0) {
        showToast(`Successfully deleted ${successCount} projects!`, 'success');
      } else {
        showToast(
          `Deleted ${successCount} projects, ${failCount} failed.`,
          failCount > 0 ? 'warning' : 'success',
        );
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      showToast('Failed to delete selected projects.', 'error');
    } finally {
      setBulkDeleting(false);
      setShowBulkDeleteModal(false);
    }
  };

  const handleEditClick = project => {
    setSelectedProject(project);
    setIsEditModalOpen(true);
  };

  const handlePreviewClick = project => {
    setSelectedProject(project);
    setIsPreviewModalOpen(true);
  };

  const handleDeleteClick = project => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedType('all');
    setCurrentPage(1);
  };

  const getCategoryIcon = category => {
    switch (category) {
      case 'branding':
        return '🎨';
      case 'product':
        return '📱';
      case 'character':
        return '👤';
      case 'scene':
        return '🏞️';
      case 'concept':
        return '💡';
      default:
        return '✨';
    }
  };

  const getTypeIcon = type => {
    switch (type) {
      case 'project':
        return '📁';
      case 'random':
        return '🎲';
      default:
        return '📄';
    }
  };

  const handleUploadImage = async (file, tempId) => {
    try {
      const result = await uploadSingleImage(file, tempId);
      showToast('Image uploaded successfully!', 'success');
      return result;
    } catch (error) {
      console.error('Upload error:', error);
      showToast(error.message || 'Failed to upload image', 'error');
      throw error;
    }
  };

  const handleDeleteTempImage = async publicId => {
    try {
      const result = await deleteTempImage(publicId);
      showToast('Temporary image deleted successfully!', 'success');
      return result;
    } catch (error) {
      console.error('Delete temp image error:', error);
      showToast(error.message || 'Failed to delete temporary image', 'error');
      throw error;
    }
  };

  const handleDeleteImage = async (projectIdOrPublicId, isTemp = false) => {
    try {
      if (isTemp) {
        const publicId = projectIdOrPublicId;
        const result = await deleteTempImage(publicId);
        showToast('Image deleted from Cloudinary!', 'success');
        return result;
      }

      const projectId = projectIdOrPublicId;
      if (projectId === -1 || projectId === null) {
        showToast('Invalid project ID', 'error');
        return;
      }

      const result = await deleteProjectImage(projectId, projectIdOrPublicId);
      await refresh();
      // Refresh counts after image delete (image count changes but project count stays)
      await fetchCounts();
      showToast('Image deleted successfully!', 'success');
      return result;
    } catch (error) {
      console.error('Delete error:', error);
      showToast(error.message || 'Failed to delete image', 'error');
      throw error;
    }
  };

  const isIllustrationTab = activeTab === 'illustration';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text)' }}>
              Work Management
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
              Manage portfolio projects and illustrations - {totalProjects}{' '}
              {isIllustrationTab ? 'illustrations' : 'projects'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {selectedProjects.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm" style={{ color: 'var(--muted)' }}>
                  {selectedProjects.length} selected
                </span>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                  title="Clear selection"
                >
                  <FiX className="w-4 h-4" style={{ color: 'var(--text)' }} />
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkDeleteModal(true)}
                  disabled={bulkDeleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50"
                >
                  {bulkDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 className="w-4 h-4" />
                      Delete Selected
                    </>
                  )}
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={async () => {
                await refresh();
                await fetchCounts();
              }}
              className="p-2 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
              title="Refresh"
            >
              <FiRefreshCw
                className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
              />
            </button>
            <button
              type="button"
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90"
              style={{
                backgroundColor: isIllustrationTab ? '#ec4899' : 'var(--text)',
                color: isIllustrationTab ? '#ffffff' : 'var(--bg)',
              }}
            >
              <FiPlus className="w-4 h-4" />
              New {isIllustrationTab ? 'Illustration' : 'Project'}
            </button>
          </div>
        </div>

        {/* ============================================= */}
        {/* TABS — with instant badge counts */}
        {/* ============================================= */}
        <div
          className="flex items-center gap-1 mb-6 p-1 rounded-xl"
          style={{
            backgroundColor: 'var(--border-light)',
            border: '1px solid var(--border)',
            width: 'fit-content',
          }}
        >
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const count = getTabCount(tab.id);
            const isLoadingCount =
              count === 0 &&
              (tab.id === 'illustration'
                ? illustrationHook.loading
                : portfolioHook.loading);

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: isActive ? 'var(--bg)' : 'transparent',
                  color: isActive ? tab.color : 'var(--muted)',
                  boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center"
                  style={{
                    backgroundColor: isActive
                      ? `${tab.color}20`
                      : 'var(--border)',
                    color: isActive ? tab.color : 'var(--muted)',
                  }}
                >
                  {isLoadingCount ? (
                    <span className="inline-block w-2 h-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    count
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div
            className="p-4 rounded-xl border"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              Total {isIllustrationTab ? 'Illustrations' : 'Projects'}
            </p>
            <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
              {totalProjects}
            </p>
          </div>
          <div
            className="p-4 rounded-xl border"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              Total Images
            </p>
            <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
              {totalImages}
            </p>
          </div>
          <div
            className="p-4 rounded-xl border"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              <FiHeart className="inline w-4 h-4 text-red-500 mr-1" />
              Likes
            </p>
            <p className="text-2xl font-bold text-red-500">{totalLikes}</p>
          </div>
          <div
            className="p-4 rounded-xl border"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              <FiEye className="inline w-4 h-4 text-blue-500 mr-1" />
              Views
            </p>
            <p className="text-2xl font-bold text-blue-500">{totalViews}</p>
          </div>
          <div
            className="p-4 rounded-xl border"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              <FiMousePointer className="inline w-4 h-4 text-green-500 mr-1" />
              Clicks
            </p>
            <p className="text-2xl font-bold text-green-500">{totalClicks}</p>
          </div>
          <div
            className="p-4 rounded-xl border"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              <FiShare2 className="inline w-4 h-4 text-purple-500 mr-1" />
              Shares
            </p>
            <p className="text-2xl font-bold text-purple-500">{totalShares}</p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <FiSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: 'var(--muted)' }}
              />
              <input
                type="text"
                placeholder={`Search ${isIllustrationTab ? 'illustrations' : 'projects'} by title, description, or tags...`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${showFilters ? 'bg-black/5 dark:bg-white/5' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
              >
                <FiFilter className="w-4 h-4" />
                Filters
                {(selectedCategory !== 'all' ||
                  selectedType !== 'all' ||
                  searchTerm) && (
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                )}
              </button>
              <div
                className="flex items-center gap-1 border rounded-lg p-1"
                style={{ borderColor: 'var(--border)' }}
              >
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-all ${viewMode === 'grid' ? 'bg-black/10 dark:bg-white/10' : ''}`}
                  title="Grid View"
                >
                  <FiGrid className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-all ${viewMode === 'list' ? 'bg-black/10 dark:bg-white/10' : ''}`}
                  title="List View"
                >
                  <FiList className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div
              className="mt-4 p-4 rounded-xl border animate-fade-in-up"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 rounded-lg border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {getCategoryIcon(cat.id)} {cat.name} ({cat.count})
                    </option>
                  ))}
                </select>
                <select
                  value={selectedType}
                  onChange={e => setSelectedType(e.target.value)}
                  className="px-3 py-2 rounded-lg border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  {types.map(type => (
                    <option key={type.id} value={type.id}>
                      {getTypeIcon(type.id)} {type.name} ({type.count})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between mt-4">
                <div className="text-xs" style={{ color: 'var(--muted)' }}>
                  {filteredProjects.length} results found
                </div>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm hover:underline"
                  style={{ color: 'var(--muted)' }}
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Selection Info Bar */}
        {selectedProjects.length > 0 && (
          <div
            className="mb-4 p-3 rounded-lg flex items-center justify-between"
            style={{
              backgroundColor: 'var(--border-light)',
              border: '1px solid var(--border)',
            }}
          >
            <span className="text-sm" style={{ color: 'var(--text)' }}>
              <strong>{selectedProjects.length}</strong> item
              {selectedProjects.length > 1 ? 's' : ''} selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-sm hover:underline"
              style={{ color: 'var(--muted)' }}
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Projects Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="animate-spin rounded-full h-12 w-12 border-b-2"
              style={{ borderColor: 'var(--text)' }}
            />
          </div>
        ) : currentProjects.length === 0 ? (
          <div
            className="text-center py-20 rounded-xl border"
            style={{ borderColor: 'var(--border)' }}
          >
            <div
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
              style={{ backgroundColor: 'var(--border-light)' }}
            >
              {isIllustrationTab ? (
                <FiPenTool
                  className="w-10 h-10"
                  style={{ color: 'var(--muted)' }}
                />
              ) : (
                <FiGrid
                  className="w-10 h-10"
                  style={{ color: 'var(--muted)' }}
                />
              )}
            </div>
            <h3
              className="text-xl font-semibold mb-2"
              style={{ color: 'var(--text)' }}
            >
              No {isIllustrationTab ? 'illustrations' : 'projects'} found
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
              {searchTerm ||
              selectedCategory !== 'all' ||
              selectedType !== 'all'
                ? 'No items match your current filters.'
                : `No ${isIllustrationTab ? 'illustrations' : 'projects'} yet. Click "New ${isIllustrationTab ? 'Illustration' : 'Project'}" to get started!`}
            </p>
            {(searchTerm ||
              selectedCategory !== 'all' ||
              selectedType !== 'all') && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 rounded-lg border hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentProjects.map(project => {
              const isSelected = selectedProjects.includes(project.id);
              return (
                <div key={project.id} className="relative group">
                  <ProjectCard
                    project={project}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                    onPreview={handlePreviewClick}
                    isSelected={isSelected}
                    onSelect={() => toggleProjectSelection(project.id)}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="overflow-x-auto rounded-xl border"
            style={{ borderColor: 'var(--border)' }}
          >
            <table className="min-w-full">
              <thead>
                <tr
                  className="border-b"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    <button
                      type="button"
                      onClick={toggleSelectAll}
                      className="flex items-center gap-2 hover:opacity-70 transition-all"
                      style={{ color: 'var(--text)' }}
                    >
                      {isAllSelected ? (
                        <FiCheckSquare className="w-4 h-4" />
                      ) : (
                        <FiSquare className="w-4 h-4" />
                      )}
                      <span>Select</span>
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Images
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-red-500">
                    ❤️ Likes
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-blue-500">
                    👁️ Views
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-green-500">
                    🖱️ Clicks
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-purple-500">
                    🔗 Shares
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentProjects.map(project => {
                  const isSelected = selectedProjects.includes(project.id);
                  return (
                    <tr
                      key={project.id}
                      className={`border-b transition-colors ${
                        isSelected ? 'bg-black/5 dark:bg-white/5' : ''
                      }`}
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggleProjectSelection(project.id)}
                          className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                          style={{ color: 'var(--text)' }}
                        >
                          {isSelected ? (
                            <FiCheckSquare className="w-4 h-4" />
                          ) : (
                            <FiSquare className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={
                              project.thumbnailUrl ||
                              project.images?.[0]?.url ||
                              project.imageUrl
                            }
                            alt={project.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <div>
                            <p
                              className="font-medium line-clamp-1"
                              style={{ color: 'var(--text)' }}
                            >
                              {project.title}
                            </p>
                            {project.featured && (
                              <span className="text-xs text-yellow-500">
                                ⭐ Featured
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                          {project.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="px-2 py-1 rounded-full text-xs"
                          style={{
                            backgroundColor:
                              project.type === 'random'
                                ? 'rgba(139, 92, 246, 0.2)'
                                : 'rgba(59, 130, 246, 0.2)',
                            color:
                              project.type === 'random' ? '#a78bfa' : '#60a5fa',
                          }}
                        >
                          {getTypeIcon(project.type)}{' '}
                          {project.type || 'project'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <FiImage
                            className="w-3 h-3"
                            style={{ color: 'var(--muted)' }}
                          />
                          <span
                            className="text-sm"
                            style={{ color: 'var(--text)' }}
                          >
                            {project.images?.length ||
                              (project.imageUrl ? 1 : 0)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <FiHeart className="w-3 h-3 text-red-500" />
                          <span
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            {project.likes || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <FiEye className="w-3 h-3 text-blue-500" />
                          <span
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            {project.views || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <FiMousePointer className="w-3 h-3 text-green-500" />
                          <span
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            {project.clicks || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <FiShare2 className="w-3 h-3 text-purple-500" />
                          <span
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            {project.shares || 0}
                          </span>
                        </div>
                      </td>
                      <td
                        className="px-4 py-3 text-sm"
                        style={{ color: 'var(--muted)' }}
                      >
                        {new Date(project.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handlePreviewClick(project)}
                            className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5"
                            title="Preview"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditClick(project)}
                            className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5"
                            title="Edit"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(project)}
                            className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500"
                            title="Delete"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            >
              <FiChevronLeft className="w-4 h-4" /> Previous
            </button>
            <div className="flex items-center gap-2">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-lg transition-all duration-200 ${
                      currentPage === pageNum
                        ? 'bg-black text-white dark:bg-white dark:text-black'
                        : 'hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                    style={{
                      color:
                        currentPage === pageNum ? undefined : 'var(--text)',
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() =>
                setCurrentPage(prev => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            >
              Next <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {filteredProjects.length > currentProjects.length && (
          <div
            className="mt-4 p-3 rounded-lg text-center text-sm"
            style={{
              backgroundColor: 'var(--border-light)',
              border: '1px solid var(--border)',
            }}
          >
            <span style={{ color: 'var(--muted)' }}>
              Showing {currentProjects.length} of {filteredProjects.length}{' '}
              matching items (Page {currentPage} of {totalPages})
            </span>
          </div>
        )}
      </div>

      {/* Modals — with forced workType based on active tab */}
      <ProjectModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddProject}
        onUploadImage={handleUploadImage}
        onDeleteImage={handleDeleteImage}
        onDeleteTempImage={handleDeleteTempImage}
        isEditing={false}
        saving={saving}
        projectId={null}
        defaultWorkType={activeTab}
      />
      <ProjectModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onSave={handleEditProject}
        onUploadImage={handleUploadImage}
        onDeleteImage={handleDeleteImage}
        onDeleteTempImage={handleDeleteTempImage}
        isEditing={true}
        saving={saving}
        projectId={selectedProject?.id}
      />
      <ProjectPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
      />

      {/* Delete Modals */}
      {showDeleteModal && projectToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            className="relative w-full max-w-md rounded-xl p-6"
            style={{ backgroundColor: 'var(--bg)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <FiTrash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3
                className="text-lg font-semibold mb-2"
                style={{ color: 'var(--text)' }}
              >
                Delete {isIllustrationTab ? 'Illustration' : 'Project'}
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                Are you sure you want to delete "{projectToDelete.title}"? This
                action cannot be undone.
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteProject}
                  disabled={isDeletingSingle}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-all disabled:opacity-50"
                >
                  {isDeletingSingle ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </span>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            className="relative w-full max-w-md rounded-xl p-6"
            style={{ backgroundColor: 'var(--bg)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <FiTrash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3
                className="text-lg font-semibold mb-2"
                style={{ color: 'var(--text)' }}
              >
                Delete {selectedProjects.length} Items
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                Are you sure you want to delete {selectedProjects.length}{' '}
                selected item{selectedProjects.length > 1 ? 's' : ''}? This
                action cannot be undone.
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-all disabled:opacity-50"
                >
                  {bulkDeleting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </span>
                  ) : (
                    'Delete All'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectsManagement;
